/**
 * The two landing stills, and the stylesheets they share with the moving
 * versions.
 *
 * WHY THIS IS ITS OWN MODULE. Both cinematic pieces are lazy chunks, and a
 * lazy chunk's CSS arrives with the chunk. If the Suspense fallback lived
 * inside WaveField.tsx or CloudCorridor.tsx it would render before its own
 * stylesheet did, so the fallback for a missing picture would be an unstyled
 * hole, which is worse than the thing it is standing in for. Keeping the stills
 * here, eagerly, means the fallback is painted correctly on the first frame and
 * the moving components import the same two stylesheets, so there is exactly one
 * definition of what the picture looks like.
 *
 * These are the SAME frame in all four cases the storyboard names: reduced
 * motion, a coarse pointer, Save-Data, and a device that refuses WebGL. There
 * is no second art direction to maintain, and there is no rendered poster to go
 * stale when the founder picks a direction.
 *
 * WHY TerminalPanel IS IMPORTED EAGERLY HERE, WHICH IS A REAL COST AND A
 * DELIBERATE ONE. Corridor v2 put live terminal panels into the rotation, so
 * two of the six cards in the composed still are panels. The still IS the
 * reduced motion, Save-Data and small phone experience; it is also the Suspense
 * fallback that stands in for the corridor chunk. If the panel component were
 * lazy, those two cards would be holes in exactly the picture that exists so
 * there are no holes, and they would be holes on the first frame of the fold.
 * So the panels ride in this module's chunk with the rest of the still, on the
 * same argument the paragraph above makes about the stylesheets. What it buys
 * is that a reader who asked for no motion gets a finished terminal rather than
 * an empty plate; what it costs is measured in the handover.
 *
 * Both are decoration. aria-hidden, no focus stop, no meaning a sighted person
 * gets and a screen reader user does not.
 */

import { DENSITY, STILL_STOPS, cardBox, sample } from '../../lib/corridor';
import type { StreamImage } from './CloudCorridor';
import { TerminalPanel } from './TerminalPanel';

import '../../styles/components/wave-field.css';
import '../../styles/components/cloud-corridor.css';

/**
 * The sky at rest: one gradient through the same five atmosphere stops the
 * shader ramps through, plus the same vignette. Zero bytes, and correct in both
 * directions and both themes without a single branch.
 */
export function SkyStill() {
  return (
    <span className="wave" data-mode="still" aria-hidden="true">
      <span className="wave__still" />
    </span>
  );
}

export interface CorridorStillProps {
  /**
   * The six cards of the composed frame, near to far, right rail then left.
   *
   * Callers hand it a manifest in its own rotation order and the composition
   * falls out of the slicing: entries 0 to 2 are the right rail, near to far,
   * and entries 3 to 5 are the left. Both manifests are ordered so that the six
   * entries this reaches compose a real frame rather than whatever the rotation
   * happened to leave there.
   *
   * The desktop sixteen give the right rail a runner at reading size, a
   * terminal panel behind him and a cloud plate at the back, and the left rail
   * a product screen up front, a second runner and a second panel. The phone
   * nine give the right rail the live web address at reading size, a runner
   * behind it and the first prompt at the back, and the left rail a runner at
   * reading size, the build behind him and a cloud plate at the back. Both
   * frames carry one card of each kind, a person on each rail, and nothing of a
   * kind facing itself at any of the three depths. The only saturated pixels in
   * either are the status lamps inside the screens and the panels.
   *
   * The phone frame is mirrored from the desktop one, runner on the left rather
   * than the right, and that is arithmetic rather than taste: position 3 is the
   * only reading size slot a runner can hold on a nine card list without the
   * flight pairing two silhouettes across the axis. The proof is on
   * STREAM_MANIFEST_LEAN.
   *
   * Empty is a finished state, not a placeholder. Six token plates at three
   * depths is a reviewable corridor with no image fetches at all, which is what
   * the Suspense fallback wants while the real component is still in flight.
   */
  images?: readonly StreamImage[];
}

/**
 * The corridor at rest: three plates per rail, at three authored depths on the
 * real path, so the still is a frame of the actual corridor rather than a
 * drawing of one.
 *
 * IT IS AUTHORED, NOT FROZEN. The reference component's own reduced motion path
 * paused eighteen cards wherever their negative delays happened to leave them,
 * which is an arbitrary clump. These six positions were chosen, and the result
 * is the same frame in all four cases: a reader who asked for no motion, a
 * phone at 390px, Save-Data, and a device that refuses WebGL.
 */
export function CorridorStill({ images = [] }: CorridorStillProps) {
  const { axis, path } = DENSITY.still;
  const box = cardBox(path, axis);

  return (
    <div className="corridor" data-density="still" data-state="arrived" aria-hidden="true">
      <div
        className="corridor__stage"
        style={{ perspective: `${path.perspective}cqw`, perspectiveOrigin: `50% ${axis}%` }}
      >
        <div className="corridor__space">
          {([1, -1] as const).map((dir, rail) =>
            STILL_STOPS.map((u, stop) => {
              const image = images[rail * STILL_STOPS.length + stop];
              return (
                <div
                  key={`${dir}-${u}`}
                  className="corridor__card"
                  // Which kind of card this is, for the near card dissolve in
                  // cloud-corridor.css. It matters more here than in the moving
                  // corridor: two of these six plates are panels at rest, they
                  // are being read rather than glimpsed, and the gradient used
                  // to lie across the bottom line of the ones that end on a
                  // sentence.
                  data-card={image?.panel ? 'panel' : image?.src ? 'image' : 'plate'}
                  style={{ ...box, transform: sample(dir, u, path) }}
                >
                  {/* A silhouette card with a clip renders as its still here,
                      with no branch and no video element, because the still is
                      the poster a clip would have shown anyway. That is what
                      makes this frame the honest answer to reduced motion,
                      Save-Data and a 390px phone: it is not the corridor with
                      the motion subtracted, it is the frame every one of those
                      readers was always going to be given.

                      A panel renders SETTLED and static here, which is the same
                      thing active={false} means everywhere else: the sentence
                      has finished typing, the bar is full, every row has
                      arrived. TerminalPanel has no second render path for it,
                      because its base stylesheet IS the finished frame and the
                      animation names are attached only under the active flag.
                      So the still costs no animation at all and needs no
                      reduced motion branch of its own. */}
                  {image?.panel ? (
                    <TerminalPanel id={image.panel} active={false} />
                  ) : image?.src ? (
                    <img
                      className="corridor__img"
                      src={image.src}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  ) : null}
                </div>
              );
            }),
          )}
        </div>
      </div>
    </div>
  );
}
